import React, { useState, useRef, useEffect } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { Button, Icon } from '@arnsbogroup/react-voicearchive'
import { FormattedMessage } from "react-intl"
import messages from "./messages"
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'

// PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

const EmbeddedPdf = ({ pdfUrl }) => {
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageWidth, setPageWidth] = useState(0)

  const containerRef = useRef(null)

  useEffect(() => {
    if (!containerRef.current) return

    const updateWidth = () => setPageWidth(containerRef.current.offsetWidth)
    updateWidth()

    const resizeObserver = new ResizeObserver(() => {
      updateWidth()
    })
    resizeObserver.observe(containerRef.current)

    return () => resizeObserver.disconnect()
  }, [])

  if (!pdfUrl) return null

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)
    setPageNumber(1)
  }

  const goToPreviousPage = () =>
    setPageNumber((p) => Math.max(p - 1, 1))

  const goToNextPage = () =>
    setPageNumber((p) => Math.min(p + 1, numPages))

  const handleDownload = async () => {
    try {
      const response = await fetch(pdfUrl)
      const blob = await response.blob()

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'document.pdf'
      document.body.appendChild(link)
      link.click()

      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed', error)
    }
  }

  return (
    <div
      ref={containerRef}
      className=" relative w-full h-full items-center justify-center bg-gray-50"
    >
      <div className="sticky top-4 z-10 h-0 flex justify-end pr-4">
        <Icon
          className="p-2 rounded-full text-primary bg-gray-100 shadow hover:bg-primary hover:text-white cursor-pointer"
          aria-label="Download"
          icon={['fas', 'download']}
          onClick={handleDownload}
        />
      </div>
      <Document
        file={pdfUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={<FormattedMessage {...messages.loading}/>}
        error={<FormattedMessage {...messages.error}/>}
      >
        <Page
          pageNumber={pageNumber}
          width={pageWidth}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
      </Document>

      {numPages > 1 && (
        <div className="mt-2 pb-2 flex items-center justify-center space-x-4">
          <Button
            onClick={goToPreviousPage}
            disabled={pageNumber <= 1}
            size="sm"
          >
            <FormattedMessage {...messages.previous}/>
          </Button>
          <FormattedMessage
            {...messages.pageCount}
            values={{
              pageNumber: pageNumber,
              numPages: numPages
            }}
            tagName="span"
          />
          <Button
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
            size="sm"
          >
            <FormattedMessage {...messages.next}/>
          </Button>
        </div>
      )}
    </div>
  )
}

export default EmbeddedPdf
